import React, { useState } from 'react';
import { Lead } from '@/types';
import { cn, getEngagementScoreColor, getPipelineStageColor, formatCurrency } from '@/utils';

interface LeadCardProps {
  lead: Lead;
  onStageChange?: (leadId: string, newStage: string) => void;
}

export const LeadCard: React.FC<LeadCardProps> = ({ lead, onStageChange: _onStageChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getPriorityColor = () => {
    if (lead.engagementScore >= 80) return 'border-emerald-500';
    if (lead.engagementScore >= 60) return 'border-amber-500';
    return 'border-slate-600';
  };

  const getLoanPurposeIcon = (purpose: string) => {
    switch (purpose) {
      case 'purchase':
        return '🏠';
      case 'refinance':
        return '🔄';
      case 'cash_out':
        return '💰';
      default:
        return '📋';
    }
  };

  return (
    <div 
      className={cn(
        'bg-slate-800 border rounded-lg p-4 hover:bg-slate-700/50 transition-all cursor-pointer',
        getPriorityColor()
      )}
      onClick={() => setIsExpanded(!isExpanded)}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-semibold text-slate-50">
            {lead.firstName} {lead.lastName}
          </h3>
          <p className="text-sm text-slate-400">
            {lead.email}
          </p>
        </div>
        <div className={cn(
          'px-2 py-1 rounded text-xs font-medium border',
          getPipelineStageColor(lead.stage)
        )}>
          {lead.stage}
        </div>
      </div>

      {/* Loan Intent */}
      <div className="flex items-center space-x-2 text-sm text-slate-300 mb-2">
        <span>{getLoanPurposeIcon(lead.loanIntent.purpose)}</span>
        <span className="capitalize">{lead.loanIntent.purpose}</span>
        {lead.loanIntent.loanAmount && (
          <>
            <span>•</span>
            <span>{formatCurrency(lead.loanIntent.loanAmount)}</span>
          </>
        )}
      </div>

      {/* Location */}
      <div className="text-sm text-slate-400 mb-2">
        📍 {lead.zipCode}
      </div>

      {/* Engagement Summary */}
      <div className="flex items-center justify-between text-sm mb-3">
        <div className="flex items-center space-x-2">
          <span>📧</span>
          <span className="text-slate-300">Last touch: {lead.lastTouch}</span>
        </div>
        <div className="flex items-center space-x-1">
          <span>⭐</span>
          <span className={cn('font-medium', getEngagementScoreColor(lead.engagementScore))}>
            Score: {lead.engagementScore}
          </span>
        </div>
      </div>

      {/* Next Action */}
      <div className="border-t border-slate-600 pt-3">
        <div className="text-sm text-slate-300 mb-1">
          Next: {lead.nextAction}
        </div>
        <div className="text-xs text-slate-400">
          ⏰ {lead.nextActionTime}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex space-x-2 mt-3">
        <button 
          className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-500"
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(true);
          }}
        >
          View Full
        </button>
        <button 
          className="text-xs bg-slate-600 text-slate-300 px-3 py-1 rounded hover:bg-slate-500"
          onClick={(e) => {
            e.stopPropagation();
            // Handle quick edit
          }}
        >
          Quick Edit
        </button>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-slate-600 space-y-3">
          <div>
            <h4 className="text-sm font-medium text-slate-300 mb-2">
              Contact Information
            </h4>
            <div className="text-sm text-slate-400 space-y-1">
              <div>Phone: {lead.phone}</div>
              <div>Source: {lead.source}</div>
              <div>Channel: {lead.channel}</div>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium text-slate-300 mb-2">
              Sequence Information
            </h4>
            <div className="text-sm text-slate-400 space-y-1">
              <div>Sequence: {lead.sequenceType}</div>
              <div>Created: {new Date(lead.createdAt).toLocaleDateString()}</div>
            </div>
          </div>

          <div className="flex space-x-2">
            <button className="text-xs bg-emerald-600 text-white px-3 py-1 rounded hover:bg-emerald-500">
              Send Message
            </button>
            <button className="text-xs bg-amber-600 text-white px-3 py-1 rounded hover:bg-amber-500">
              Schedule Call
            </button>
            <button className="text-xs bg-red-600 text-white px-3 py-1 rounded hover:bg-red-500">
              Flag for Review
            </button>
          </div>
        </div>
      )}
    </div>
  );
};